/* eslint-disable check-file/filename-naming-convention */
import React from 'react';
import { useSpring, easings } from '@react-spring/three';
import { FrontSide, Matrix3, Matrix4, Vector3 } from 'three';
import { Model3DXML } from '@gkn-ili/cad-model';
import { useView3DRepState, View3DRepState } from '../../../context/context3D';
import { useFrame } from '@react-three/fiber';
import { computeCenterOfCenters } from '@gkn-ili/cad-model/dist/meshHelpers/computeCenter';

function getColor(defaultColor: { r: number; g: number; b: number }, state: View3DRepState): number {
  if (state.actionState !== 'none') {
    return state.color;
  }

  // convert default color to hex
  const rHex = (Math.floor(defaultColor.r * 255) & 0xff) << 16;
  const gHex = (Math.floor(defaultColor.g * 255) & 0xff) << 8;
  const bHex = Math.floor(defaultColor.b * 255) & 0xff;

  return rHex + gHex + bHex;
}

function getTranslationRotationFromMatrix4(m: number[]): { translation: Vector3; rotScale: Matrix3 } {
  const translation = new Vector3(m[9] ?? 0, m[10] ?? 0, m[11] ?? 0);

  const rotScale = new Matrix3(m[0], m[1], m[2], m[3], m[4], m[5], m[6], m[7], m[8]).transpose();

  return { translation, rotScale };
}

export default function App3DXML3DReference(
  props: React.PropsWithoutRef<{
    reference3D: Model3DXML;
    relativeMatrix?: number[];
    hidden?: boolean;
    path: string[];
  }>,
): JSX.Element {
  const personalRef = [...props.path];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupRef = React.useRef<any>(null);

  const view3DRepState = useView3DRepState(personalRef);

  const [spring] = useSpring(() => {
    if (view3DRepState.actionState === 'animated') {
      return {
        from: {
          x: view3DRepState.offsetPosition[0],
          y: view3DRepState.offsetPosition[1],
          z: view3DRepState.offsetPosition[2],
        },
        to: { x: 0, y: 0, z: 0 },
        config: { duration: 2000, easing: easings.easeOutSine },
        loop: true,
        reset: true,
      };
    }

    return { from: { x: 0, y: 0, z: 0 }, to: { x: 0, y: 0, z: 0 }, reset: true, config: { duration: 0 } };
  }, [view3DRepState.actionState]);

  const representations = props.reference3D.representations.map((rep) =>
    rep.instance.visuals.map((visual, j) =>
      visual.faces.map((face, k) => (
        <mesh key={rep.id + '_' + j + '_' + k} name={rep.name} visible={view3DRepState.visible}>
          <bufferGeometry onUpdate={(self) => self.computeVertexNormals()}>
            <bufferAttribute
              attach="attributes-position"
              array={visual.vertexBuffer.positions}
              itemSize={3}
              count={visual.vertexBuffer.positions.length / 3}
            />
            <bufferAttribute attach="index" array={face.indices} itemSize={1} count={face.indices.length} />
          </bufferGeometry>
          <meshStandardMaterial side={FrontSide} color={getColor(face.color, view3DRepState)} />
        </mesh>
      )),
    ),
  );

  const children = Object.values(props.reference3D.children).map((child) => (
    <App3DXML3DReference
      key={child.id}
      relativeMatrix={child.relativeMatrix}
      reference3D={child.instance}
      path={[...personalRef, child.name]}
    />
  ));

  function getTransform(offsetPosition?: [number, number, number]): Matrix4 {
    const transform = new Matrix4();
    if (props.relativeMatrix) {
      const m = props.relativeMatrix;
      const { translation, rotScale } = getTranslationRotationFromMatrix4(m);
      const offset = new Vector3(offsetPosition?.[0], offsetPosition?.[1], offsetPosition?.[2]);
      translation.add(offset);

      transform.multiply(new Matrix4().setFromMatrix3(rotScale));
      transform.setPosition(translation);
    }

    if (props.path.length === 0) {
      transform.scale(new Vector3(1, -1, 1));
    }
    return transform;
  }
  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.matrix = getTransform([spring.x.get(), spring.y.get(), spring.z.get()]);
      groupRef.current.updateMatrixWorld(true);

      if (view3DRepState.ref3DObject) {
        // compute center for annotation
        const center = computeCenterOfCenters(
          props.reference3D.representations.flatMap((k) => k.instance.visuals.map((v) => v.center)),
        );
        const meshCenter = new Vector3(center[0], center[1], center[2]);
        const worldCenter: Vector3 = groupRef.current.localToWorld(meshCenter);
        view3DRepState.ref3DObject.center.set(worldCenter.x, worldCenter.y, worldCenter.z);
      }
    }
  });

  return (
    <>
      <group matrix={getTransform()} matrixAutoUpdate={false} ref={groupRef}>
        {children}
        {representations}
      </group>
    </>
  );
}
